import { YStack } from 'tamagui';

import PSBox from '@/components/ui/PSBox';
import PSButton from '@/components/ui/PSButton';
import PSText from '@/components/ui/PSText';
import { openURL } from '@/src/utils/linkingUtils';

/**
 * ExternalTicketTier — the whole "Ticket" section on an EXTERNAL event's page
 * (molecule). The counterpart to [[TicketTier]], not a variant of it.
 *
 * An external event is one we don't sell: there are no ticket tiers on the wire
 * (`slots[].ticket_tiers` comes back `[]`), so there is nothing to step, gate or
 * add to a cart. What's left is the one number we do know — `price_from` — and
 * the link out. That's why this is a separate component rather than a branch
 * inside `TicketTier`: they share a heading and a surface, and NOTHING else.
 * `TicketTier` is a switch over the server's `bookability` verdict; this card
 * has no verdict, no quantity and no per-slot junction to read, so folding the
 * two together would mean every prop of the ticketed card arriving `null` here
 * just to reach a branch that ignores them.
 *
 * Same reason it doesn't reuse [[TierNotice]] for the white block inside:
 * a notice is RECESSED (grey on the page's white, hairline-bordered, an H2
 * title, an inline CTA) because it's a footnote under a tier that's still the
 * point. Here the pitch IS the point, so it's raised instead — white on the
 * grey card, no border, a `Title` headline and a full-width CTA. Those are four
 * inverted style props, i.e. a fork of every line of TierNotice, which is the
 * "edit-every-ternary" variant the guide says not to add.
 *
 * ── Why only the OUTER box is a [[PSBox]] ──
 * The outer card is the house's grey bordered box exactly as `RequestNotice`
 * wears it, so it's the preset plus the two props that differ. The INNER one
 * deliberately isn't, and that's not an oversight: PSBox's whole point is the
 * hairline border, and a raised block can't have one — so using it there would
 * mean passing `borderWidth={0}` to cancel the one default it exists for, which
 * the token linter rejects outright (`borderWidth` must be a specific token;
 * `0` is not one). Needing an eslint-disable to un-border a box is the signal
 * that it isn't that box. Plain `YStack`, no suppression.
 *
 * ── Pure ──
 * Two props and no state: the price is already formatted by the server
 * (`PSPrice.display`, so currency/decimals are never assembled here) and the
 * URL is the event's `externalUrl`. The copy is hardwired because it's the
 * house's own promise, not the caller's — there is one external-event pitch and
 * every external event makes it, so a `title`/`body` prop pair would be config
 * with exactly one value (the [[PendingPaymentBanner]] precedent).
 *
 * The press opens the link itself rather than taking an `onPress`: "where this
 * goes" is the URL, which is already the prop, so a handler would only let a
 * caller send the button somewhere the card doesn't say it goes.
 */
export default function ExternalTicketTier({
  displayPrice,
  url,
}: {
  /** The server's own formatted price — `event.priceFrom.display`. */
  displayPrice: string;
  /** Where the tickets actually are — `event.externalUrl`. */
  url: string;
}) {
  return (
    <PSBox
      bg="$backgroundSecondary"
      gap="$md"
      borderColor={'$color.backgroundSecondary'}
    >
      <YStack gap="$xs">
        <PSText.Body1 color="$textDark">{displayPrice}</PSText.Body1>
        <PSText.Body3 color="$textDarkMuted">Per ticket</PSText.Body3>
      </YStack>

      {/* Raised, not recessed — see the docstring. `$background` over
        `$backgroundSecondary` is the inverse of every other card on this page,
        which is exactly what makes the pitch read as the foreground. */}
      <YStack bg="$background" rounded="$base" p="$md" gap="$md">
        <PSText.Title color="$textDark">
          Recommended by Putting Scene.
        </PSText.Title>
        <PSText.Body2 color="$textDark">
          {"We'll take you to where you can book it."}
        </PSText.Body2>
        <PSButton title="Get tickets" fullWidth onPress={() => openURL(url)} />
      </YStack>
    </PSBox>
  );
}
